import { type PropsWithChildren, startTransition } from 'react';
import cn from 'clsx';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { type SearchState, SearchStoreProvider } from '@pkgs/components/hooks/stores/search/SearchStoreProvider';
import IconCross from '@pkgs/components/assets/icons/xsmall/Cross.svg';
import IconArrowDown from '@pkgs/components/assets/icons/xsmall/Arrow-Thin-Down.svg';
import { Copy } from '@ev-tech/ui-core-components/Copy';

import { LifestyleFilterDropdownActions } from './LifestyleFilterDropdownActions';
import { Popover, PopoverRoot, PopoverTrigger } from '../Popover/Popover';

import styles from './LifestyleFilterDropdown.module.css';

type LifestyleFilterDropdownProps = PropsWithChildren<{
  label: string;
  count?: number;
  className?: string;
  isActive?: boolean;
  onClose?: () => void;
  'data-testid'?: string;
  defaultState: Partial<SearchState>;
}>;

export const LifestyleFilterDropdown = ({
  label,
  count,
  children,
  isActive,
  className,
  defaultState,
  'data-testid': dataTestId,
}: LifestyleFilterDropdownProps) => {
  const resetState = useSearchStore((state) => state.resetState);
  const getStateSnapshot = useSearchStore((state) => state.getStateSnapshot);
  const applyState = useSearchStore((state) => state.applyState);

  const handleApplyClick = (state: SearchState) => {
    startTransition(() => {
      applyState({ state });
    });
  };

  return (
    <PopoverRoot modal="trap-focus">
      <PopoverTrigger
        render={
          <button className={cn(styles.button, className)} data-testid={dataTestId} data-active={isActive}>
            <Copy className={styles.labelCopy} as="span" text={label} />
            {Boolean(count) && <Copy as="span" text={'+' + count} />}
            {isActive ? (
              <IconCross className={styles.crossIcon} onClick={() => startTransition(() => resetState(defaultState))} />
            ) : (
              <IconArrowDown className={styles.arrowDown} />
            )}
          </button>
        }
      />
      <Popover aria-label={label} className={styles.dialog}>
        <SearchStoreProvider initialState={getStateSnapshot()} namespace="lifestyle-filter-dropdown">
          {children}
          <LifestyleFilterDropdownActions onApply={handleApplyClick} defaultState={defaultState} />
        </SearchStoreProvider>
      </Popover>
    </PopoverRoot>
  );
};
